/**
 * The profile's left column: how to reach the contact, and what else is
 * recorded about where they are.
 *
 * The old "Also reachable at" list put every address on the record in one
 * column, and for a contact read off Outlook mail most of them were
 * `<guid>@namprd13.prod.outlook.com`: Exchange's internal name for a user in a
 * message header. Nobody can write to those. They are split out here, by the
 * platform's own rule (the server's `routingAddresses`), into a group that is
 * collapsed by default, says in a sentence what it is, and can be shown. Nothing
 * is hidden for good and nothing is removed from the record.
 */
import { useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ChevronDown, ChevronRight, MapPin } from 'lucide-react'

import { recordKindLabel, type CrmContact } from '@/services/crm'
import { externalHref, linkLabel, mailtoHref, mapsHref, telHref } from './contactLinks'
import {
  NOT_ON_RECORD,
  formatDate,
  kindLabel,
  labelForField,
  labelledPhones,
  relativeTime,
} from './crmFields'
import { IdentitySection } from './ProfileSections'
import { sourceLine, locationText, type AddressGroups, type MergedRecord, count } from './profileFacts'
import { Absent, Card, Disclosure, Field, FOCUS, LINK, Quiet } from './profileUi'

const CHIP = 'rounded border border-stone-700 bg-black/30 px-1.5 py-0.5 text-[11px] text-stone-300'

// ── How to reach ──────────────────────────────────────────────────────────────

export function HowToReach({ contact, groups }: { contact: CrmContact; groups: AddressGroups }) {
  const phones = labelledPhones(contact).filter((row) => row.value)
  const address = contact.address ?? locationText(contact)
  const addressHref = mapsHref(contact.address ?? contact.location ?? null)
  const linkedIn = externalHref(contact.enrichment?.linkedIn)
  const website = externalHref(contact.enrichment?.website)
  const tags = Array.from(new Set([...(contact.tags ?? []), ...(contact.capacities ?? [])]))

  // The source stamped on `email` describes the address the record holds as
  // its primary; it says nothing about a different one promoted over a routing id.
  const emailSource = groups.primary
    ? groups.primary.toLowerCase() === (contact.email ?? '').toLowerCase()
      ? (sourceLine(contact, 'email', groups.primary) ?? 'Source not recorded')
      : (sourceLine({ ...contact, fieldSources: {}, editedFields: [] }, 'email', groups.primary) ?? 'Source not recorded')
    : null

  return (
    <Card title="How to reach" kicker>
      <div className="space-y-4">
        <Field
          label="Email"
          note={
            groups.primary
              ? emailSource
              : groups.routing.length > 0
                ? 'Only system routing IDs are on file, and nobody can write to those. Add a mailbox with Edit.'
                : null
          }
        >
          {groups.primary ? (
            <a href={mailtoHref(groups.primary) ?? undefined} className={`${LINK} break-all`}>
              {groups.primary}
            </a>
          ) : (
            <Absent>No mailbox on record</Absent>
          )}
        </Field>

        {phones.length > 0 ? (
          phones.map((row) => {
            const href = telHref(row.value)
            return (
              <Field key={row.field} label={row.label ? `${row.label} line` : 'Phone'} note={sourceLine(contact, row.field, row.value)}>
                {href ? (
                  <a href={href} className={LINK}>
                    {row.value}
                  </a>
                ) : (
                  row.value
                )}
              </Field>
            )
          })
        ) : (
          <Field label="Phone">
            <Absent />
          </Field>
        )}

        <Field label="Address" note={address ? sourceLine(contact, 'address', contact.address) : null}>
          {address ? (
            addressHref ? (
              <a href={addressHref} target="_blank" rel="noopener noreferrer" className={LINK}>
                <MapPin size={11} className="mr-1 inline align-[-1px] text-stone-500" aria-hidden />
                {address}
              </a>
            ) : (
              address
            )
          ) : (
            <Absent />
          )}
        </Field>

        {contact.enrichment?.linkedIn ? (
          <Field label="LinkedIn">
            {linkedIn ? (
              <a href={linkedIn} target="_blank" rel="noopener noreferrer" className={LINK}>
                {linkLabel(contact.enrichment.linkedIn)}
              </a>
            ) : (
              contact.enrichment.linkedIn
            )}
          </Field>
        ) : null}
        {contact.enrichment?.website ? (
          <Field label="Website">
            {website ? (
              <a href={website} target="_blank" rel="noopener noreferrer" className={LINK}>
                {linkLabel(contact.enrichment.website)}
              </a>
            ) : (
              contact.enrichment.website
            )}
          </Field>
        ) : null}
      </div>

      {contact.source === 'Deal Capture' ? (
        <p className="mt-4 text-[11px] leading-snug text-stone-500">
          Captured from the deals. {contact.roleEvidence ?? 'No record on file establishes which side of the table this is.'}
        </p>
      ) : null}
      {(contact.editedFields ?? []).length > 0 ? (
        <p className="mt-2 text-[11px] leading-snug text-stone-500">
          Edited by {contact.editedBy ?? 'a person'}: {contact.editedFields.map(labelForField).join(', ')}. Capture leaves these alone.
        </p>
      ) : null}
      {tags.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {tags.map((tag) => (
            <span key={tag} className={CHIP}>
              {tag}
            </span>
          ))}
        </div>
      ) : null}
    </Card>
  )
}

// ── Other addresses and the routing IDs ───────────────────────────────────────

/** The group that used to be most of the "Also reach" list. */
export function RoutingIds({ ids }: { ids: string[] }) {
  const [shown, setShown] = useState(false)
  const label = `${count(ids.length, 'system routing ID')}`
  return (
    <div className="border-t border-stone-800/80 pt-3">
      <button
        type="button"
        aria-expanded={shown}
        onClick={() => setShown((v) => !v)}
        className={`flex w-full items-center justify-between gap-2 rounded px-1 py-1 text-left ${FOCUS}`}
      >
        <span className="text-[12px]">
          <span className="font-semibold text-stone-200">{label}</span>{' '}
          <span className="text-stone-500">{shown ? 'shown' : 'hidden'}</span>
        </span>
        <span className="flex shrink-0 items-center gap-1 text-[12px] text-amber-300">
          {shown ? 'Hide' : 'Show'}
          {shown ? <ChevronDown size={12} aria-hidden /> : <ChevronRight size={12} aria-hidden />}
        </span>
      </button>
      <p className="px-1 text-[11px] leading-snug text-stone-500">
        Outlook creates long internal codes when mail passes through Exchange. Nobody can write to them, so nothing is
        sent to them. They are kept on the record as a trace of where mail passed through.
      </p>
      {shown ? (
        <ul
          aria-label="System routing IDs"
          className="mt-2 max-h-44 space-y-1 overflow-y-auto px-1 font-mono text-[10.5px] leading-snug text-stone-500 scrollbar-thin"
        >
          {ids.map((id) => (
            <li key={id} className="break-all">
              {id}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function OtherAddresses({ groups }: { groups: AddressGroups }) {
  if (groups.alternates.length === 0 && groups.routing.length === 0) return null
  return (
    <Card title="Other addresses" kicker>
      {groups.alternates.length > 0 ? (
        <>
          <p className="mb-3 text-[11px] leading-snug text-stone-500">
            Other mailboxes recorded for this contact. The record does not keep when each one was first seen.
          </p>
          <ul className="space-y-2">
            {groups.alternates.map((alt) => (
              <li key={alt.address} className="rounded-lg border border-stone-800 px-3 py-2">
                <a href={mailtoHref(alt.address) ?? undefined} className={`${LINK} break-all text-[12.5px]`}>
                  {alt.address}
                </a>
                <div className="mt-0.5 text-[11px] text-stone-500">
                  {alt.sameDomainAsPrimary ? 'Same domain as the main address' : `At ${alt.domain || 'another domain'}`}
                  {alt.source ? ` · ${alt.source}` : ''}
                </div>
              </li>
            ))}
          </ul>
        </>
      ) : (
        <p className="text-[11px] leading-snug text-stone-500">No other mailbox is recorded for this contact.</p>
      )}
      {groups.routing.length > 0 ? (
        <div className={groups.alternates.length > 0 ? 'mt-4' : 'mt-3'}>
          <RoutingIds ids={groups.routing} />
        </div>
      ) : null}
    </Card>
  )
}

// ── Linked records and record details ────────────────────────────────────────

export function LinkedRecords({
  contact,
  merged,
  onOpenContact,
  onNavigate,
}: {
  contact: CrmContact
  merged: MergedRecord[]
  onOpenContact?: (id: string) => void
  /** Called when the merge-log link is followed, so the window can close. */
  onNavigate: () => void
}) {
  const firm = contact.company
  const firmId = contact.companyId
  const rows: { label: string; node: ReactNode }[] = []
  if (firm) {
    rows.push({
      label: 'Firm',
      node:
        firmId && onOpenContact ? (
          <button type="button" onClick={() => onOpenContact(firmId)} className={`${LINK} text-right`}>
            {firm}
          </button>
        ) : firmId ? (
          <a href={`/crm?focus=${encodeURIComponent(firmId)}&open=1`} className={`${LINK} text-right`}>
            {firm}
          </a>
        ) : (
          <span className="text-right text-stone-200">{firm}</span>
        ),
    })
  }
  if (merged.length > 0) {
    const first = merged.slice(0, 3)
    rows.push({
      label: 'Merged from',
      node: (
        <span className="text-right text-stone-200">
          {first.map((m) => m.name).join(', ')}
          {merged.length > first.length ? ` and ${merged.length - first.length} more` : ''}
        </span>
      ),
    })
  }
  if (rows.length === 0) return null
  const latest = merged[0]
  return (
    <Quiet title="Linked records">
      <dl className="space-y-1.5 text-[12px]">
        {rows.map((row) => (
          <div key={row.label} className="flex items-baseline justify-between gap-3">
            <dt className="shrink-0 text-stone-500">{row.label}</dt>
            <dd className="min-w-0 break-words">{row.node}</dd>
          </div>
        ))}
      </dl>
      {latest ? (
        <p className="mt-2 text-[11px] leading-snug text-stone-500">
          Last merge {relativeTime(latest.at)}
          {latest.automatic ? ', by the dedup pass' : latest.actor ? `, by ${latest.actor}` : ''}.{' '}
          <Link to="/crm?view=activity" onClick={onNavigate} className={LINK}>
            Undo is in the merge log
          </Link>
          .
        </p>
      ) : null}
    </Quiet>
  )
}

/** Identity, source and status: what the vitals strip used to carry, now below the fold. */
export function RecordDetails({ contact }: { contact: CrmContact }) {
  return (
    <Disclosure title="Record details">
      <div className="[&>section]:!border-0 [&>section]:!px-0 [&>section]:!py-1">
        <dl className="mb-2 grid grid-cols-[76px_minmax(0,1fr)] gap-x-3 gap-y-1 text-[12px]">
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Record</dt>
          <dd className="min-w-0 break-all font-mono text-[11px] text-stone-300">
            {contact.id} · {recordKindLabel(contact.id)}
          </dd>
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Kind</dt>
          <dd className="text-stone-300">{kindLabel(contact.kind, contact.isEntity)}</dd>
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Source</dt>
          <dd className="text-stone-300">{contact.source || NOT_ON_RECORD}</dd>
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Status</dt>
          <dd className="text-stone-300">
            {contact.status || NOT_ON_RECORD}
            {contact.retired ? ' · Retired' : contact.needsReview ? ' · Unsure entry' : ''}
          </dd>
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">First seen</dt>
          <dd className="text-stone-300">{contact.firstSeen ? formatDate(contact.firstSeen) : NOT_ON_RECORD}</dd>
          <dt className="font-mono text-[9px] uppercase tracking-[0.14em] text-stone-500">Last seen</dt>
          <dd className="text-stone-300">{contact.lastContact ? formatDate(contact.lastContact) : NOT_ON_RECORD}</dd>
        </dl>
        <IdentitySection contact={contact} dense summarised />
      </div>
    </Disclosure>
  )
}
